// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// TanStack Query hooks (design 3.1, 3.5).
import { useQuery } from '@tanstack/react-query';
import { TERMINAL_RUN_STATUSES } from '@bda/shared';
import { api } from './client';

type SetProposalStatusResponse = Awaited<ReturnType<typeof api.getSetProposal>>;

export const POLL_MS = 3000;

export const keys = {
  blueprints: (q: object) => ['blueprints', q] as const,
  blueprint: (id: string) => ['blueprint', id] as const,
  version: (id: string, v: number) => ['version', id, v] as const,
  run: (id: string) => ['run', id] as const,
  result: (id: string) => ['result', id] as const,
  runs: (q: object) => ['runs', q] as const,
  pkg: (id: string) => ['package', id] as const,
  myPackages: ['packages', 'mine'] as const,
  shared: ['packages', 'shared'] as const,
  sets: (q: object) => ['sets', q] as const,
  set: (id: string) => ['set', id] as const,
  setVersion: (id: string, v: number) => ['setVersion', id, v] as const,
  proposal: (runId: string) => ['setProposal', runId] as const,
};

/** Most pages followed for one list (100 evaluated items each), so a list is complete up to 5,000 items. */
export const MAX_PAGES = 50;

/**
 * Follows nextCursor until the list is complete (requirements 2.1, 11.6). Blueprint queries read up to 100 items
 * per page and apply search and filters after the read, so a filtered first page can be short or even empty while
 * later pages still match. Returns nextCursor only when MAX_PAGES was reached.
 */
export async function fetchAllPages<T>(
  fetchPage: (cursor: string | undefined) => Promise<{ items: T[]; nextCursor: string | null }>,
  maxPages = MAX_PAGES,
): Promise<{ items: T[]; nextCursor: string | null }> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < maxPages; page++) {
    const res = await fetchPage(cursor);
    items.push(...res.items);
    if (!res.nextCursor) return { items, nextCursor: null };
    cursor = res.nextCursor;
  }
  return { items, nextCursor: cursor ?? null };
}

export const useBlueprints = (q: Omit<Parameters<typeof api.listBlueprints>[0], 'cursor'>) =>
  useQuery({
    queryKey: keys.blueprints(q),
    queryFn: () => fetchAllPages((cursor) => api.listBlueprints({ ...q, cursor })),
  });

export const useBlueprint = (id: string | undefined) =>
  useQuery({ queryKey: keys.blueprint(id ?? ''), queryFn: () => api.getBlueprint(id!), enabled: !!id });

export const useVersion = (id: string, v: number | null) =>
  useQuery({ queryKey: keys.version(id, v ?? 0), queryFn: () => api.getVersion(id, v!), enabled: v !== null && v > 0 });

/** Polls every 3 s until the run reaches a terminal status (requirement 5.4). */
export const useRun = (id: string | undefined) =>
  useQuery({
    queryKey: keys.run(id ?? ''),
    queryFn: () => api.getRun(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && TERMINAL_RUN_STATUSES.includes(q.state.data.status) ? false : POLL_MS),
  });

export const useRunResult = (id: string | undefined, enabled: boolean) =>
  useQuery({ queryKey: keys.result(id ?? ''), queryFn: () => api.getResult(id!), enabled: !!id && enabled });

export const useRuns = (q: Parameters<typeof api.listRuns>[0]) =>
  useQuery({ queryKey: keys.runs(q), queryFn: () => api.listRuns(q) });

export const usePackage = (id: string | undefined) =>
  useQuery({
    queryKey: keys.pkg(id ?? ''),
    queryFn: () => api.getPackage(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.package.status === 'BUILDING' ? POLL_MS : false),
  });

// ---------- Blueprint sets (blueprint-sets design "Front end") ----------

/** The author's sets; `archived: 'true'` for the "Archived" filter. Follows nextCursor like the blueprint list. */
/** `enabled: false` skips the request, for pages that offer sets only to some users (Req 12.3, 14.2). */
export const useSets = (q: Omit<Parameters<typeof api.listSets>[0], 'cursor'> = {}, opts: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: keys.sets(q),
    queryFn: () => fetchAllPages((cursor) => api.listSets({ ...q, cursor })),
    enabled: opts.enabled ?? true,
  });

export const useSet = (id: string | undefined) =>
  useQuery({ queryKey: keys.set(id ?? ''), queryFn: () => api.getSet(id!), enabled: !!id });

const SETTLED_PROPOSAL_STATUSES: readonly string[] = ['READY', 'FAILED'];

/**
 * Whether a Packet_Suggest_Run's proposal will not change any more: its run failed, or its proposal is READY or
 * FAILED. A SUCCEEDED run is not enough, as the proposal is built after BDA finishes (Req 9.6).
 */
export function isProposalSettled(data: SetProposalStatusResponse | undefined): boolean {
  if (!data) return false;
  if (data.run.status === 'FAILED') return true;
  return data.proposal.status !== null && SETTLED_PROPOSAL_STATUSES.includes(data.proposal.status);
}

/** Polls every 3 s until the proposal settles (`isProposalSettled`), as `useRun` does for runs (Req 9.6). */
export const useSetProposal = (runId: string | undefined) =>
  useQuery({
    queryKey: keys.proposal(runId ?? ''),
    queryFn: () => api.getSetProposal(runId!),
    enabled: !!runId,
    refetchInterval: (q) => (isProposalSettled(q.state.data) ? false : POLL_MS),
  });
